import type { B2BConfiguration, B2BThemeEffect, B2BThemeName } from '@@fire-b2b/types/ClientConfiguration'
import type { HexColorString, RgbColorString } from '@@fire-base/types/utils/ColorString'

export type BrandTheme = {
  themeName: B2BThemeName
  isBranded: boolean
}

export type BrandThemeConfig = {
  themeworld: BrandTheme
  look: BrandTheme
  error: BrandTheme
  colorHex?: HexColorString
  colorRgb?: RgbColorString
  lightBackgroundGradientColorRgb?: RgbColorString
  darkBackgroundGradientColorRgb?: RgbColorString
}

export const useBrandThemeStore = defineStore('brandTheme', () => {
  const { selectedBrand } = storeToRefs(useBrandsStore())

  const b2bConfig = computed<B2BConfiguration | null>(() => {
    return selectedBrand.value?.b2bConfiguration ?? null
  })

  const configuredTheme = computed<BrandThemeConfig>(() => {
    const themeworld = themeEffectToTheme(b2bConfig.value?.themeworld?.theme, b2bConfig.value?.themeworld?.effect)
    const look = themeEffectToTheme(b2bConfig.value?.look?.theme, b2bConfig.value?.look?.effect)
    const error = themeEffectToTheme(b2bConfig.value?.error?.theme, b2bConfig.value?.error?.effect)
    const colorHex = selectedBrand.value?.color
    const colorRgb = colorHex ? hexToRgbString(colorHex) : undefined

    let darkBackgroundGradientColorRgb = colorRgb
    let lightBackgroundGradientColorRgb = colorRgb

    // NOTE: special case for pure black color -> use contrasting color
    if (colorHex?.toLowerCase() === '#000000') {
      darkBackgroundGradientColorRgb = '255, 255, 255'
      lightBackgroundGradientColorRgb = '0, 0, 0'
    }

    return {
      themeworld,
      look,
      error,
      colorHex,
      colorRgb,
      darkBackgroundGradientColorRgb,
      lightBackgroundGradientColorRgb,
    }
  })

  function themeEffectToTheme(theme?: B2BThemeName, effect?: B2BThemeEffect): BrandTheme {
    const themeName = theme ?? 'light'

    return {
      themeName,
      isBranded: effect === 'branded',
    }
  }

  return {
    configuredTheme,
  }
})
